import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import { DOMAIN_RATES_QUERY, UPDATE_DOMAIN_RATES_MUTATION } from '../graphql/domain';

const SERVER_URL = process.env.REACT_APP_API_HOST || 'http://localhost:8080';

interface RateField {
  key: string;
  label: string;
  // Most rates are dollar amounts stored as cents. A markup multiplier is
  // stored the same way (scaled by 100) but displays/edits as "x2.00", not "$".
  unit?: '$' | 'x';
}

interface RateGroup {
  title: string;
  fields: RateField[];
}

const RATE_GROUPS: RateGroup[] = [
  {
    title: 'Lining & Interlining',
    fields: [
      { key: 'liningRegularCost',      label: 'Lining — Regular per yard' },
      { key: 'liningBlackoutCost',     label: 'Lining — Blackout per yard' },
      { key: 'liningSheerCost',        label: 'Lining — Sheer per yard' },
      { key: 'interliningBumpCost',    label: 'Interlining — Bump per yard' },
      { key: 'interliningFlannelCost', label: 'Interlining — Flannel per yard' },
    ],
  },
  {
    title: 'Drapery',
    fields: [
      { key: 'draperyCutLaborCost',    label: 'Fabrication per cut' },
      { key: 'rateDrapeInstallPerFt',  label: 'Installation per foot' },
      { key: 'rateDrapeInstallMin',    label: 'Installation minimum' },
      { key: 'rateHospitalTrackPerFt', label: 'Hospital track per foot' },
      { key: 'draperyCtmHardwareCost', label: 'CTM (Cut to Measure) per pair' },
      { key: 'draperyTrimLaborCostPerYard',   label: 'Trim labor per yard' },
    ],
  },
  {
    title: 'Roman Shades',
    fields: [
      { key: 'romanShadeLaborCostPerSqFt',   label: 'Fabrication per sq ft' },
      { key: 'romanShadeInstallPerWidth',     label: 'Installation per width (fabric cut)' },
      { key: 'romanShadeInstallMin',          label: 'Installation minimum' },
      { key: 'romanShadeBoardSmallCost',      label: 'Covered board ≤96"' },
      { key: 'romanShadeBoardLargeCost',      label: 'Covered board >96"' },
      { key: 'romanShadeVelcroCostPerYard',      label: 'Velcro per yard' },
      { key: 'romanShadeTrimLaborCostPerYard',   label: 'Trim labor per yard' },
      { key: 'romanShadeCordCleatCost',   label: 'Operating system — Cord & Cleat' },
      { key: 'romanShadeRolleaseCost',    label: 'Operating system — Rollease' },
      { key: 'romanShadeSomfyCost',       label: 'Operating system — Somfy' },
    ],
  },
  {
    title: 'Valances',
    fields: [
      { key: 'rateValanceInstallPerFt', label: 'Installation per foot' },
      { key: 'rateValanceInstallMin',   label: 'Installation minimum' },
      { key: 'ratePleatedFabPerCut',    label: 'Pleated fabrication per cut' },
    ],
  },
  {
    title: 'Shirred Valance',
    fields: [
      { key: 'shirredValanceFabPerCut',             label: 'Fabrication per cut' },
      { key: 'shirredValanceInstallPerFt',           label: 'Installation per fabric width' },
      { key: 'shirredValanceInstallMin',             label: 'Installation minimum' },
      { key: 'shirredValanceBoardSmallCost',         label: 'Board & brackets (≤96")' },
      { key: 'shirredValanceBoardLargeCost',         label: 'Board & brackets (>96")' },
      { key: 'shirredValanceVelcroCostPerYard',      label: 'Velcro per yard' },
      { key: 'shirredValanceTrimLaborCostPerYard',   label: 'Trim labor per yard' },
    ],
  },
  {
    title: 'Balloon / Austrian',
    fields: [
      { key: 'rateBalloonFabPerSqft',  label: 'Balloon fabrication per sq ft' },
      { key: 'rateAustrianFabPerSqft', label: 'Austrian fabrication per sq ft' },
    ],
  },
  {
    title: 'Roller Shades',
    fields: [
      { key: 'rollerShadeSmallRodPerFootCost',            label: 'Rod per foot (small, ≤50")' },
      { key: 'rollerShadeMediumRodPerFootCost',           label: 'Rod per foot (medium, 50–75")' },
      { key: 'rollerShadeHeavyRodPerFootCost',            label: 'Rod per foot (heavy, >75")' },
      { key: 'rollerShadeSmallClutchAndIdleEndSetCost',   label: 'Clutch & idle end set (small, ≤50")' },
      { key: 'rollerShadeMediumClutchAndIdleEndSetCost',  label: 'Clutch & idle end set (medium, 50–75")' },
      { key: 'rollerShadeLargeClutchAndIdleEndSetCost',   label: 'Clutch & idle end set (large, 75–100")' },
      { key: 'rollerShadeExtraClutchAndIdleEndSetCost',   label: 'Clutch & idle end set (extra, >100")' },
      { key: 'rollerShadeWandMotorCost',                  label: 'Wand motor (inc. motor + charger)' },
      { key: 'rollerShadeBottomBarStandardPerFootCost',   label: 'Bottom bar per foot (standard)' },
      { key: 'rollerShadeBottomBarBlackoutPerFootCost',   label: 'Bottom bar per foot (blackout)' },
      { key: 'rollerShadeFasciaPerFoot3InCost',           label: 'Fascia per foot (3")' },
      { key: 'rollerShadeFasciaPerFoot4InCost',           label: 'Fascia per foot (4")' },
      { key: 'rollerShadeFasciaBrackets3InCost',          label: 'Fascia brackets (3")' },
      { key: 'rollerShadeFasciaBrackets4InCost',          label: 'Fascia brackets (4")' },
      { key: 'rollerShadeEndcapsStandardCost',            label: 'Endcaps (standard)' },
      { key: 'rollerShadeEndcapsBlackoutCost',            label: 'Endcaps (blackout)' },
      { key: 'rollerShadeChainsCoversCost',               label: 'Chains/covers' },
      { key: 'rollerShadeTapeCost',                       label: 'Tape' },
      { key: 'rollerShadeShippingCost',                   label: 'Shipping' },
      { key: 'rollerShadeFabricationNarrowCost',          label: 'Fabrication (cut width ≤50")' },
      { key: 'rollerShadeFabricationWideCost',            label: 'Fabrication (cut width >50")' },
      { key: 'rollerShadeInstallationCost',               label: 'Installation per shade' },
      { key: 'rollerShadeRetailPriceMarkup',               label: 'Retail markup (applies to Shade line only)', unit: 'x' },
    ],
  },
  {
    title: 'Motors',
    fields: [
      { key: 'rateSomfyMotor',    label: 'Somfy standard motor' },
      { key: 'rateRolleaseMotor', label: 'Rollease motor' },
    ],
  },
  {
    title: 'Trim & Materials',
    fields: [
      { key: 'rateTrimLaborPerYd', label: 'Trim labor per yard' },
      { key: 'rateVelcroPerYd',    label: 'Velcro per yard' },
    ],
  },
  {
    title: 'Soft Goods',
    fields: [
      { key: 'rateDelivery',              label: 'Delivery (flat)' },
      { key: 'ratePillowZipper',          label: 'Pillow zipper' },
      { key: 'rateBolsterZipper',         label: 'Bolster zipper' },
      { key: 'rateCushionZipperShort',    label: 'Cushion zipper ≤48"' },
      { key: 'rateCushionZipperLong',     label: 'Cushion zipper >48"' },
      { key: 'rateCushionLaborThin',      label: 'Cushion labor per inch (≤4" thick)' },
      { key: 'rateCushionLaborThick',     label: 'Cushion labor per inch (>4" thick)' },
      { key: 'rateCushionInsertDacron',   label: 'Dacron-wrapped insert per inch' },
      { key: 'rateCushionInsertDownWrap', label: 'Down-wrapped insert per inch' },
      { key: 'rateCushionInsertDown',     label: 'Down insert per inch' },
      { key: 'rateDuvetZipper',           label: 'Duvet zipper' },
    ],
  },
  {
    title: 'Misc',
    fields: [
      { key: 'rateMeasureCharge', label: 'Measure charge (flat)' },
    ],
  },
];

interface QbStatus {
  connected: boolean;
  realmId?: string;
  connectedAt?: string;
}

function QuickBooksSection() {
  const [status, setStatus] = useState<QbStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Populated by the /qb/callback redirect (?qb=connected or ?qb=error&message=…)
  const params = new URLSearchParams(window.location.search);
  const callbackResult = params.get('qb');
  const callbackMessage = params.get('message');

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${SERVER_URL}/qb/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error('Could not load QuickBooks status');
        setStatus(await resp.json());
      })
      .catch((err) => setStatusError(err.message));
  }, []);

  const handleConnect = () => {
    const token = localStorage.getItem('authToken');
    window.location.href = `${SERVER_URL}/qb/connect?token=${encodeURIComponent(token || '')}`;
  };

  return (
    <fieldset style={{ marginBottom: 24, border: '1px solid #ddd', borderRadius: 4, padding: '12px 16px' }}>
      <legend style={{ fontWeight: 600, padding: '0 4px' }}>QuickBooks</legend>
      {callbackResult === 'connected' && (
        <p style={{ color: 'green', marginBottom: 8, fontSize: 14 }}>QuickBooks connected successfully.</p>
      )}
      {callbackResult === 'disconnected' && (
        <p style={{ marginBottom: 8, fontSize: 14 }}>QuickBooks has been disconnected.</p>
      )}
      {callbackResult === 'error' && (
        <p style={{ color: 'red', marginBottom: 8, fontSize: 14 }}>
          QuickBooks connection failed{callbackMessage ? `: ${callbackMessage}` : '.'}
        </p>
      )}
      {statusError && <p style={{ color: 'red', marginBottom: 8, fontSize: 14 }}>{statusError}</p>}
      {status?.connected ? (
        <div style={{ fontSize: 14 }}>
          <p style={{ marginBottom: 8 }}>
            Connected to QuickBooks company <strong>{status.realmId}</strong>
            {status.connectedAt && ` (since ${new Date(status.connectedAt).toLocaleDateString()})`}.
          </p>
          <button type="button" onClick={handleConnect} style={{ padding: '6px 16px', fontSize: 13 }}>
            Reconnect
          </button>
        </div>
      ) : (
        <div style={{ fontSize: 14 }}>
          <p style={{ marginBottom: 8 }}>
            Connect your QuickBooks account to send quotes as estimates.
          </p>
          <button type="button" onClick={handleConnect} style={{ padding: '6px 16px', fontSize: 13 }}>
            Connect to QuickBooks
          </button>
        </div>
      )}
    </fieldset>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const domainId = user?.domain_id;

  const { data, loading, error } = useQuery(DOMAIN_RATES_QUERY, {
    variables: { id: domainId },
    skip: !domainId,
  });

  const [updateDomain, { loading: saving, error: saveError }] = useMutation(
    UPDATE_DOMAIN_RATES_MUTATION
  );

  const [fields, setFields] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.domainById) {
      const d = data.domainById;
      const init: Record<string, string> = {};
      for (const group of RATE_GROUPS) {
        for (const { key } of group.fields) {
          init[key] = (d[key] / 100).toFixed(2);
        }
      }
      setFields(init);
    }
  }, [data]);

  const handleChange = (key: string, value: string) => {
    setFields(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Record<string, number> = {};
    for (const [key, val] of Object.entries(fields)) {
      const parsed = parseFloat(val);
      if (!isNaN(parsed)) patch[key] = Math.round(parsed * 100);
    }
    await updateDomain({ variables: { id: domainId, patch } });
    setSaved(true);
  };

  if (!domainId) return <div style={{ padding: 40 }}>Not logged in.</div>;
  if (loading) return <div style={{ padding: 40 }}>Loading rates…</div>;
  if (error) return <div style={{ padding: 40 }}>Error loading rates: {error.message}</div>;

  return (
    <div style={{ padding: 40, maxWidth: 720 }}>
      <h1 style={{ marginBottom: 24 }}>Settings</h1>
      <QuickBooksSection />
      <h2 style={{ marginBottom: 16 }}>Pricing Rates</h2>
      {saveError && <p style={{ color: 'red', marginBottom: 12 }}>Save failed: {saveError.message}</p>}
      {saved && <p style={{ color: 'green', marginBottom: 12 }}>Rates saved.</p>}
      <form onSubmit={handleSubmit}>
        {RATE_GROUPS.map(group => (
          <fieldset key={group.title} style={{ marginBottom: 24, border: '1px solid #ddd', borderRadius: 4, padding: '12px 16px' }}>
            <legend style={{ fontWeight: 600, padding: '0 4px' }}>{group.title}</legend>
            {group.fields.map(({ key, label, unit }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ width: 300, marginRight: 8, fontSize: 14 }}>{label}</label>
                <span style={{ marginRight: 2, fontSize: 14 }}>{unit ?? '$'}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={fields[key] ?? ''}
                  onChange={e => handleChange(key, e.target.value)}
                  style={{ width: 100, padding: '2px 4px', fontSize: 14 }}
                />
              </div>
            ))}
          </fieldset>
        ))}
        <button type="submit" disabled={saving} style={{ padding: '8px 24px', fontSize: 14 }}>
          {saving ? 'Saving…' : 'Save Rates'}
        </button>
      </form>
    </div>
  );
}
